import { useState, useEffect, useRef } from "react";
import type { FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { adminAPI } from "../services/admin.api";
import { useLanguage } from "../../../context/LanguageContext";

export interface ContentFormData {
  title: string;
  description: string;
  category: string | null;
  video?: File | null;
  video_url?: string;
  metrics?: { views?: number; likes?: number; shares?: number };
}

const initialForm: ContentFormData = {
  title: "",
  description: "",
  category: null,
  video: null,
  video_url: "",
  metrics: { views: 0, likes: 0, shares: 0 },
};

const VIDEO_MAX_BYTES = 50 * 1024 * 1024;

const formatBytes = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export const useContentsNew = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState<ContentFormData>(initialForm);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [videoFileName, setVideoFileName] = useState<string | null>(null);
  const [videoFileSize, setVideoFileSize] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  useEffect(() => {
    adminAPI
      .getCategories()
      .then((data) => setCategories(data.data ?? data ?? []))
      .catch(() => {});
  }, []);

  const setField = <K extends keyof ContentFormData>(
    key: K,
    value: ContentFormData[K],
  ) => setFormData((prev) => ({ ...prev, [key]: value }));

  const handleVideoUpload = (file: File) => {
    if (file.size > VIDEO_MAX_BYTES) {
      setVideoError(t("admin.contentsNew.videoTooLarge"));
      setField("video", null);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setVideoPreview(null);
      setVideoFileName(null);
      setVideoFileSize(null);
      return;
    }
    setVideoError(null);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setVideoPreview(url);
    setVideoFileName(file.name);
    setVideoFileSize(formatBytes(file.size));
    setField("video", file);
  };

  const clearVideo = () => {
    setVideoError(null);
    setField("video", null);
    setVideoFileName(null);
    setVideoFileSize(null);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setVideoPreview(null);
  };

  const doSubmit = async () => {
    if (!formData.title || !formData.description || !formData.category) {
      Swal.fire(t("swal.content.validationErrorTitle"), t("swal.content.validationErrorText"), "warning");
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const form = new FormData();
      form.append("title", formData.title);
      form.append("description", formData.description);
      form.append("category", formData.category);

      if (formData.video) {
        form.append("video", formData.video);
      } else if (formData.video_url) {
        form.append("video_url", formData.video_url);
      }

      const metrics = formData.metrics;
      if (metrics && Object.keys(metrics).length > 0) {
        form.append("metrics", JSON.stringify(metrics));
      }

      if (id) {
        await adminAPI.updateContent(id, form, (pct) => setUploadProgress(pct));
      } else {
        await adminAPI.createContent(form, (pct) => setUploadProgress(pct));
      }

      await Swal.fire({
        icon: "success",
        title: id ? t("swal.content.updatedTitle") : t("swal.content.createdTitle"),
        confirmButtonColor: "#b2d12e",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate("/admin/contents");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("admin.contentsNew.uploadFailedDefault");
      setUploadError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await doSubmit();
  };

  return {
    id,
    categories,
    loading,
    uploadProgress,
    formData,
    setField,
    videoPreview,
    videoFileName,
    videoFileSize,
    videoError,
    uploadError,
    handleVideoUpload,
    clearVideo,
    handleSubmit,
    retrySubmit: doSubmit,
  };
};
